// src/components/SetPassword.js

import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom'; // Assuming you'll use React Router
import 'animate.css';
import zxcvbn from 'zxcvbn';
import { API_BASE_URL } from '../config';

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const SetPassword = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [userInfo, setUserInfo] = useState({ username: '', email: '' });
  const [tokenValid, setTokenValid] = useState(null); // null = loading, true = valid, false = invalid
  const [tokenError, setTokenError] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  // Validate token and fetch user info on mount
  useEffect(() => {
    const validateToken = async () => {
      const fullUrl = `${API_BASE_URL}auth/validate-set-password-token/`;
      console.log('[SetPassword] Validating token…');
      console.log('[SetPassword] API_BASE_URL:', API_BASE_URL);
      console.log('[SetPassword] Request URL:', fullUrl);

      try {
        const res = await axios.get(fullUrl, {
          params: { uid, token },
        });
        setUserInfo({ username: res.data.username, email: res.data.email });
        setTokenValid(true);
      } catch (err) {
        console.error('[SetPassword] Validation error:', err);

        if (err.response) {
          const { status, data } = err.response;
          console.error('[SetPassword] Status:', status, 'Body:', data);

          if (status === 404) {
            // The endpoint itself was not found — deployment / proxy issue
            setTokenError(
              'The server could not find the password-reset endpoint (HTTP 404). ' +
              'This usually means the latest code has not been deployed, or the ' +
              'reverse-proxy is not forwarding /api/ requests correctly. ' +
              'Please contact your administrator.'
            );
          } else {
            // 400 / other — actual token validation failure
            setTokenError(data?.error || 'This invitation link is invalid or has expired.');
          }
        } else if (err.request) {
          // Request was sent but no response received (network / CORS issue)
          setTokenError(
            'Unable to reach the server. Please check your internet connection and try again. ' +
            'If the problem persists, contact your administrator for a new invitation link.'
          );
        } else {
          setTokenError('An unexpected error occurred. Please try again later.');
        }
        setTokenValid(false);
      }
    };
    validateToken();
  }, [uid, token]);

  const strength = useMemo(() => zxcvbn(passwordData.password || ''), [passwordData.password]);
  const strengthScore = strength.score; // 0-4
  const strengthText = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'][strengthScore];

  // S-01: Real-time password confirmation validation
  const passwordTooShort = passwordData.password.length > 0 && passwordData.password.length < 8;
  const passwordMismatch =
    passwordData.confirmPassword.length > 0 &&
    passwordData.password !== passwordData.confirmPassword;
  const formValid =
    passwordData.password.length >= 8 &&
    passwordData.confirmPassword.length > 0 &&
    passwordData.password === passwordData.confirmPassword;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    // Clear submit-level error when user edits fields
    if (message.type === 'danger') {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.password !== passwordData.confirmPassword) {
      setMessage({ type: 'danger', text: 'Passwords do not match.' });
      return;
    }
    if (passwordData.password.length < 8) {
        setMessage({ type: 'danger', text: 'Password must be at least 8 characters long.'});
        return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
        await axios.post(`${API_BASE_URL}auth/set-password-confirm/`, {
        uid: uid,
        token: token,
        password: passwordData.password,
      });
      setMessage({ type: 'success', text: 'Your password has been set successfully! You will be redirected to the login page shortly.' });
      
      setTimeout(() => {
        // SPA navigation to login page (no full reload)
        navigate('/');
      }, 1500);

    } catch (error) {
      const data = error.response?.data;
      const raw = data?.error ?? data?.detail;
      const errorMessage = Array.isArray(raw)
        ? raw.join(' ')
        : (raw || 'An unexpected error occurred. The link may be invalid or expired.');
      setMessage({ type: 'danger', text: errorMessage });
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-panel animate__animated animate__fadeInDown">
        {/* Branding Panel (Left side) */}
        <div className="auth-branding-panel">
          <div className="p-12 text-white">
            <h1 className="text-4xl font-bold mb-4">LegalOps</h1>
            <p className="text-lg text-gray-200">
              Set a strong password to secure your account.
            </p>
            <div className="mt-8 border-l-4 border-accent pl-4">
              <p className="text-gray-300 italic">"The secret of getting ahead is getting started."</p>
              <p className="text-gray-400 text-sm mt-1">- Mark Twain</p>
            </div>
          </div>
        </div>

        {/* Form Panel (Right side) */}
        <div className="auth-form-panel">
          <div className="max-w-md w-full space-y-6 p-10">
            {/* Loading state while validating token */}
            {tokenValid === null && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-500">Validating your invitation link…</p>
              </div>
            )}

            {/* Invalid/expired token */}
            {tokenValid === false && (
              <div className="text-center py-12 space-y-4">
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800" role="alert">
                  {tokenError}
                </div>
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800 text-sm">
                  <p className="font-semibold mb-1">What to do next</p>
                  <p>
                    Ask your firm administrator to resend the invitation from the
                    staff management page. A new link will be emailed to you.
                  </p>
                </div>
                <button
                  type="button"
                  className="auth-link"
                  onClick={() => navigate('/')}
                >
                  Go to login
                </button>
              </div>
            )}

            {/* Valid token — show form */}
            {tokenValid === true && (
              <>
                <div>
                  <h2 className="mt-6 text-center text-3xl font-extrabold text-primary">
                    Set Your Password
                  </h2>
                  <p className="mt-2 text-center text-sm text-gray-600">
                    Choose a strong password and confirm it below.
                  </p>
                </div>

                {message.text && (
                  <div
                    className={
                      message.type === 'success'
                        ? 'rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-800'
                        : 'rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800'
                    }
                    role="status"
                    aria-live="polite"
                  >
                    {message.text}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* Read-only username */}
                    <div className="input-group">
                      <span className="input-icon"><UserIcon /></span>
                      <input
                        type="text"
                        className="auth-input bg-gray-100 cursor-not-allowed"
                        value={userInfo.username}
                        readOnly
                        disabled
                        title="Username cannot be changed"
                      />
                    </div>

                    {/* Read-only email */}
                    <div className="input-group">
                      <span className="input-icon"><EmailIcon /></span>
                      <input
                        type="email"
                        className="auth-input bg-gray-100 cursor-not-allowed"
                        value={userInfo.email}
                        readOnly
                        disabled
                        title="Email cannot be changed"
                      />
                    </div>

                    {/* Password field */}
                    <div className="input-group">
                      <span className="input-icon"><LockIcon /></span>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        className="auth-input"
                        value={passwordData.password}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        placeholder="New password"
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="-mt-2" aria-live="polite" aria-atomic="true">
                      <div className="flex items-center gap-2">
                        <div
                          style={{
                            width: `${(strengthScore + 1) * 20}%`,
                            height: '8px',
                            background: ['#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#27ae60'][strengthScore],
                            borderRadius: '4px'
                          }}
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuemax={4}
                          aria-valuenow={strengthScore}
                          aria-label={`Password strength: ${strengthText}`}
                        />
                        <small className="text-gray-500">{strengthText}</small>
                      </div>
                      {strength?.feedback?.warning && (
                        <div className="text-amber-700 text-sm mt-1" role="status">{strength.feedback.warning}</div>
                      )}
                    </div>

                    {/* Confirm password field */}
                    <div className="input-group">
                      <span className="input-icon"><LockIcon /></span>
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`auth-input ${passwordMismatch ? 'border-red-400' : ''}`}
                        value={passwordData.confirmPassword}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                    </div>
                    {passwordMismatch && (
                      <p className="text-red-600 text-sm -mt-2" role="alert">Passwords do not match</p>
                    )}
                    {passwordTooShort && (
                      <p className="text-red-600 text-sm -mt-2" role="alert">Password must be at least 8 characters</p>
                    )}
                  </div>

                  <div>
                    <button type="submit" className="auth-button" disabled={loading || !formValid}>
                      {loading ? 'Setting Password…' : 'Set Password'}
                    </button>
                  </div>

                  <div className="text-sm text-center">
                    <button
                      type="button"
                      className="auth-link"
                      onClick={() => navigate('/')}
                      disabled={loading}
                    >
                      Back to login
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
